/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, April 10th 2022, 10:13:02 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { BillingConfiguration } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import Stack from "@mui/material/Stack";
import { OSActionButton } from "ui-widgets";
import { useServiceBuilderContext } from "service-builder/providers";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { COLORSCHEME } from "theme/styles";
import { useNavigate } from "react-router-dom";
import { resolveURLName } from "core/routes/utils";
import { EmptyBillingConfigurationsCTA } from "./EmptyBillingConfigurationsCTA";
import { useAPIListBillingConfigurations } from "service-builder/services/BillingConfigurationAPI";

export const BillingConfigurationListView = () => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const {
    items: billingConfigurationsList,
    isMakingAPIRequest: isFetchingBillingConfigurationsList,
    httpResponseStatusCode: fetchBillingConfigurationsListStatusCode,
    isAPIError: fetchBillingConfigurationsListHasError,
  } = useAPIListBillingConfigurations(currentProcessDefinition.id);

  const navigate = useNavigate();

  const navigateToBillingConfiguration = React.useCallback(
    (billingConfigurationId: PrimaryKeyValue<BillingConfiguration>) => {
      navigate(
        resolveURLName("billingConfiguration", { serviceId: currentProcessDefinition.id, billingConfigurationId })
      );
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, resolveURLName, currentProcessDefinition.id]
  );

  return (
    <APIListProgressWrapper
      items={billingConfigurationsList}
      showLoader={isFetchingBillingConfigurationsList}
      httpResponseStatusCode={fetchBillingConfigurationsListStatusCode}
      isAPIError={fetchBillingConfigurationsListHasError}
      noDataMessage={<EmptyBillingConfigurationsCTA />}
    >
      <Stack direction="column" spacing={2}>
        <List component="nav" aria-label="billing-configurations-list">
          {billingConfigurationsList.map((billingConfiguration) => (
            <ListItemButton color={COLORSCHEME.secondaryVariant} key={`${billingConfiguration.id}`}>
              <Grid container>
                <Grid item md={1}>
                  <ReceiptIcon color="primary" />
                </Grid>
                <Grid item md={7}>
                  <ListItemText primary={billingConfiguration.name} />
                </Grid>
                <Grid item md={4}>
                  <OSActionButton
                    actionStyle="configure"
                    variant="text"
                    onClick={() => navigateToBillingConfiguration(billingConfiguration.id)}
                  >
                    Configure Pricing
                  </OSActionButton>
                </Grid>
              </Grid>
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </APIListProgressWrapper>
  );
};
